import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useSurveyContext} from "../../context/SurveyContext";
import useGet from "../../hooks/useGet";
import {postToHub} from "../../actions/networkActions";
import {
    getErrors,
    getNrChildQuestionsForPart,
    isFormValid,
    isQuestionShown
} from "../../utils/questionnaireUtils";
import Progress from "./questions/Progress";
import Question from "./questions/Question";
import BackLink from "../BackLink";
import ThemableSvgIcon from "../theme/ThemableSvgIcon";
import usePreCruisePassenger from '../../hooks/usePreCruisePassenger';
import { Trans, useLingui } from '@lingui/react/macro';
import { navigate } from '../../utils/navigationUtils';
import { useLocation, useNavigationType } from 'react-router';

function PassengerForm(props) {
    const {survey, dispatch} = useSurveyContext();
    const [portalLoaded, setPortalLoaded] = useState(false);
    const [submitInProgress, setSubmitInProgress] = useState(false);

    const {t} = useLingui();
    const navigationType = useNavigationType();
    const location = useLocation();

    const {passengerId, type} = props.params;
    const isMedicalMobilityForm = type === "medicalAndMobility";
    const questionsUrl = `precruise/questions/${isMedicalMobilityForm ? "prerequisiteTravelInfo" : "travelInfo"}/${passengerId}`;

    const initialSectionSet = location?.state?.section !== undefined;
    const initialSection = location?.state?.section || 0;
    const [{activeSection, activePart, previousParts}, setActiveSection] = useState({activeSection: initialSection, activePart: 0, previousParts: []});
    const [submitError, setSubmitError] = useState("");

    const sections = useSelector(state => state.preCruise.sections);
    const {isTravelInfoSecured} = usePreCruisePassenger(passengerId);

    const data = useGet(questionsUrl, {});
    useEffect(() => {
        const {passenger, ...categories} = data;

        const questions = {};
        const answers = {};
        const parts = {};

        for (const category in categories) {
            questions[category] = categories[category].questions;
            parts[category] = categories[category].parts;

            if(categories[category].answers) {
                for (const questionId in categories[category].answers) {
                    answers[questionId] = categories[category].answers[questionId];
                }
            }
        }
        dispatch({
            type: "SET_SURVEY_DATA",
            passenger,
            answers,
            questions,
            parts,
            card: categories.cardDetails,
        });

    }, [data, dispatch]);


    const moveBack = React.useCallback((isBrowserBack = false) => {
        // handle back to review from the first part in case of entering the form via "edit" button on review page
        if(initialSectionSet && activePart === 0) {
            // a back press has already moved us onto the form entry itself, so pushing the review
            // url here would truncate the forward history and leave this entry armed, sending the
            // next back press forward again. Unwind instead, the entry below is the review page.
            if(isBrowserBack) {
                navigate(-1);
                return;
            }

            const reviewUrl = t({id: "/precruise/passenger"}) + `/${passengerId}` + t({id: "/review"}) + `${isMedicalMobilityForm ? "/medicalAndMobility": ""}`;

            navigate(reviewUrl);
            return;
        }

        // from the start of the form, go back to previous page from the history
        if(activeSection === 0 && activePart === 0) {
            navigate(-1);
            return;
        }

        // add the current location to the history in order
        navigate(location, {state: location.state});

        // actual moving back of the form
        if(activePart > 0) {
            setActiveSection({activeSection, activePart: activePart - 1, previousParts: previousParts.slice(0, -1)});
        }
        else {
            setActiveSection({activeSection: activeSection - 1, activePart: previousParts.slice(-1)[0], previousParts: previousParts.slice(0, -1)});
        }
    }, [activePart, activeSection, initialSectionSet]);

    // add the current location to the history, to prevent browsers back behaviour to go to previous url
    useEffect(() => {
        navigate(location, {state: location.state});
    }, []);

    // declare listen function on history and handle any POP actions (back button)
    useEffect(() => {
        if (navigationType === "POP") {
            moveBack(true);
        }
    }, [location.key, navigationType, moveBack]);

    if(!survey.passenger || !survey.passenger.passengerId) {
        return (<div className="container mx-auto mt-12">
            <span translate="no"><Trans id="loading">loading</Trans>...</span>
        </div>)
    }

    if(sections.length === 0) {
        return (<div className="container mx-auto mt-12">
            <Trans id="no_questionnaires_available">No Questionnaires available.</Trans>
        </div>)
    }

    const section = sections[activeSection];
    const questions = survey.questions[section.hubId]?.filter(isQuestionShown(survey.passenger)) || [];
    const partInfo = survey.parts[section.hubId] ? survey.parts[section.hubId][activePart] : {};
    const nrParts = survey.parts && survey.parts[section.hubId]?.length;

    const buttonEnabled = isFormValid(questions, survey.answers, survey.passenger, activePart);

    const nrQuestionsVisibleForNextPart = () => {
        const nextPart = activePart + 1;

        return questions.filter(question => question.part === nextPart).length +
        questions.reduce((sum, question) => sum + getNrChildQuestionsForPart(question, survey.answers[question.id], nextPart), 0);
    }

    async function submit() {
        if(validateForm(questions) && !submitInProgress) {
            window.scrollTo(0, 0);
            // move to next part of the same section if there are still some questions to be answered
            if((nrParts > activePart + 1) && nrQuestionsVisibleForNextPart() > 0) {
                setActiveSection({activeSection, activePart: activePart + 1, previousParts: [...previousParts, activePart]});
                return;
            }

            // submit the section data
            const body = {
                passengerId,
                category: sections[activeSection].hubId,
                answers: {}
            }
            for (let questionId of survey.activeFields) {
                const answerVal = survey.answers[questionId] === undefined ? "" : survey.answers[questionId];
                body.answers[questionId] = answerVal;
            }

            setSubmitInProgress(true);
            const submitUrl = 'precruise/questions/submitAnswers';
            const result = await postToHub(submitUrl, body);
            setSubmitInProgress(false);

            if(result !== "") {
                setSubmitError(t({id: "error_submitting_data_check_your_answers_and_try_again"}));
                return;
            }

            setSubmitError("");

            if(initialSectionSet) {
                const reviewUrl = t({id: "/precruise/passenger"}) + `/${passengerId}` + t({id: "/review"}) + `${isMedicalMobilityForm ? "/medicalAndMobility": ""}`;

                navigate(reviewUrl);
                return;
            }

            if(activeSection + 1 === sections.length) {
                // redirect user to lock their info if not locked already
                const finishUrl = isTravelInfoSecured ?
                    t({id: '/precruise/passengers'}) + `${isMedicalMobilityForm ? "/medicalAndMobility" : ""}` :
                    t({id: '/precruise/passenger'}) + `/${passengerId}` + t({id: '/lock'}) + `${isMedicalMobilityForm ? "/medicalAndMobility" : ""}`;

                navigate(finishUrl);
            }
            else {
                setActiveSection({activePart: 0, previousParts: [...previousParts, activePart], activeSection: activeSection + 1});
            }
        }
    }

    function validateForm(questions) {
        const errors = getErrors(questions, survey.answers, survey.passenger, activePart);

        dispatch({
            type: "SET_ERRORS",
            errors,
        });

        return Object.keys(errors).length === 0;
    }


    let backTitle = t({id: "back_to"})+" ";
    if(activePart > 0) {
        backTitle += survey.parts[section.hubId][activePart - 1].title;
    }
    else {
        if(initialSectionSet) {
            backTitle += t({id: "review_page"});
        }
        else {
            if(activeSection > 0) {
                const prevSectionParts = survey.parts[sections[activeSection - 1].hubId];
                backTitle += prevSectionParts[prevSectionParts.length - 1].title;
            }
        }
    }


    return (
        <>
            <div className="bg-grey-lightest-2 py-4 border-b">
                {sections.length > 1 ? (
                    <div className="container mx-auto">
                        {/*progress bar*/}
                        <Progress activeSection={activeSection}/>
                    </div>
                ) : null}
            </div>
            <div className="container mx-auto mt-4">
                { (activeSection > 0 || activePart > 0 || initialSectionSet) &&
                <div className="mr-4">
                    <button className="text-primary inline-flex items-center hover:underline" onClick={() => moveBack()}>
                        <ThemableSvgIcon name="chevron-left" className="w-4 h-4 inline"/> {backTitle}
                    </button>
                </div>
                }
                {activeSection === 0 && activePart === 0 && !initialSectionSet &&
                <div className="mr-4">
                    <BackLink
                        to={isMedicalMobilityForm ? (t({id: "/precruise/passengers"}) + '/medicalAndMobility') : t({id: "/precruise/passengers"})}
                        text={isMedicalMobilityForm ? t({id: "back_to_medical_and_mobility_information_dashboard"}) : t({id: "back_to_travel_information_dashboard"})}
                    />
                </div>
                }


                {/* section title */}
                <h1 className="my-6">
                    {partInfo.title}
                </h1>

                {/* info text for some sections*/}
                {partInfo.partInfoHtml &&
                    <div dangerouslySetInnerHTML={{__html: partInfo.partInfoHtml}} />
                }


                {/* questions */}
                {portalLoaded &&
                <div className={partInfo.style === "box" ? "bg-grey-lightest p-4 border" : ""}>
                    {questions.map((question, index) =>
                        <Question
                            key={question.id}
                            activePart={activePart}
                            isFirst={index === 0}
                            question={question}
                        />
                    )}
                </div>
                }

                <LastQuestionsPortal setPortalLoaded={setPortalLoaded}/>

                {/* info text for some sections*/}
                {partInfo.partInfoHtmlBottom &&
                <div dangerouslySetInnerHTML={{__html: partInfo.partInfoHtmlBottom}} />
                }

                {submitError &&
                <div className="mt-4 text-red">
                    {submitError}
                </div>
                }


                {(section.hubId !== "cardDetails") &&
                <div className="mt-6 mb-8 xl:mb-12">
                    <button
                        className={`action-button w-mobile ${buttonEnabled && !submitInProgress ? "action-button-enabled" : "action-button-disabled"}`}
                        onClick={submit}>
                        <Trans id="next">Next</Trans>
                    </button>
                </div>
                }


            </div>
        </>
    );
}

function LastQuestionsPortal(props) {
    useEffect(() => {
        props.setPortalLoaded(true);
    }, []);

    return (
        <div id="last-questions-portal"></div>
    )
}

export default PassengerForm;